import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, startWith, catchError, shareReplay } from 'rxjs/operators';
import { Aggregator } from '@liberty-financial/agp-shared';

import { ApiHttp } from '@core/api/api-http';
import { environment } from '@env/environment';
import { aggregator } from '@env/aggregator/aggregator';

import { IUserContract, UserList } from './user';

/**
 * Service responsible for loading and caching assignable users.
 * Ensures only one HTTP request is made per session.
 * 
 * Cache Lifecycle:
 * - Created on first `getAssignableUsers()` call
 * - Shared across all call sites via RxJS shareReplay
 * - Cleared on token network refresh (ACQUIRE_TOKEN_NETWORK_START event, ~every 50-60 minutes)
 * - Cleared on logout (ACCOUNT_REMOVED event)
 * - Cleared on account switch (ACCOUNT_ADDED event)
 * - Garbage collected when app closes
 */
@Injectable({
  providedIn: 'root'
})
export class AssignableUsersService {
  // Cached observable - created on first call and reused for all subsequent calls
  // This ensures only ONE HTTP request is made per session (until auth token refresh)
  private cachedAssignableUsers$: Observable<UserList> | null = null;

  // Separate cache for the inactive-inclusive list so the two variants never
  // clobber each other (the active-only list must stay active-only).
  private cachedAssignableUsersIncludingInactive$: Observable<UserList> | null = null;

  constructor(private http: ApiHttp) {}

  /**
   * Get assignable users. Loads once on first call, then returns cached data.
   * All subscribers share the same cached observable - only one HTTP request per auth session.
   * 
   * Cache auto-refreshes when authentication token is refreshed from the network (~every 50-60 minutes).
   * 
   * @param startWithEmpty If true, immediately emits an empty list before cached data
   * @param throwError If true, errors will propagate; if false, returns empty list on error
   */
  getAssignableUsers(
    startWithEmpty: boolean = false,
    throwError: boolean = false
  ): Observable<UserList> {
    // Create and cache the observable on first call
    if (!this.cachedAssignableUsers$) {
      // Skip, as LFAU aggregator does not call Management API
      if (aggregator.name === Aggregator.LFAU) {
        this.cachedAssignableUsers$ = of(new UserList([]));
      } else {
        this.cachedAssignableUsers$ = this.http
          .get<IUserContract[]>(
            environment.managementApiUrl,
            'assignable-users',
            ApiHttp.getStandardOptions()
          )
          .pipe(
            map((users) => new UserList(users)),
            catchError((error) => {
              console.error('Failed to load assignable users:', error);
              return of(new UserList([]));
            }),
            shareReplay({ bufferSize: 1, refCount: false })
          );
      }
    }

    // Return the cached observable (with optional modifications)
    let result = this.cachedAssignableUsers$;

    if (startWithEmpty) {
      result = result.pipe(startWith(new UserList([])));
    }

    if (!throwError) {
      result = result.pipe(catchError(() => of(new UserList([]))));
    }

    return result;
  }

  /**
   * Get assignable users of every activation status (active and inactive).
   * Calls the Management API with `includeInactive=true`. For read-only filtering
   * only (e.g. the Opportunities "Owned by" filter), never for assigning work.
   * Cached separately from the active-only list.
   *
   * @param startWithEmpty If true, immediately emits an empty list before cached data
   * @param throwError If true, errors will propagate; if false, returns empty list on error
   */
  getAssignableUsersIncludingInactive(
    startWithEmpty: boolean = false,
    throwError: boolean = false
  ): Observable<UserList> {
    if (!this.cachedAssignableUsersIncludingInactive$) {
      // Skip, as LFAU aggregator does not call Management API
      if (aggregator.name === Aggregator.LFAU) {
        this.cachedAssignableUsersIncludingInactive$ = of(new UserList([]));
      } else {
        this.cachedAssignableUsersIncludingInactive$ = this.http
          .get<IUserContract[]>(
            environment.managementApiUrl,
            'assignable-users?includeInactive=true',
            ApiHttp.getStandardOptions()
          )
          .pipe(
            map((users) => new UserList(users)),
            catchError((error) => {
              console.error('Failed to load assignable users (including inactive):', error);
              return of(new UserList([]));
            }),
            shareReplay({ bufferSize: 1, refCount: false })
          );
      }
    }

    let result = this.cachedAssignableUsersIncludingInactive$;

    if (startWithEmpty) {
      result = result.pipe(startWith(new UserList([])));
    }

    if (!throwError) {
      result = result.pipe(catchError(() => of(new UserList([]))));
    }

    return result;
  }

  /**
   * Clear the cache. Call this if you need to force a refresh.
   * 
   * This is automatically called on:
   * - Token network refresh (ACQUIRE_TOKEN_NETWORK_START event, ~every 50-60 minutes)
   * - Logout (ACCOUNT_REMOVED event)
   * - Account switch (ACCOUNT_ADDED event)
   * 
   * Manual calls are rarely needed but available if required.
   */
  clearCache(): void {
    this.cachedAssignableUsers$ = null;
    this.cachedAssignableUsersIncludingInactive$ = null;
  }
}
